// src/components/PurchaseFlow/Step/OfferStep.js
import React, { useState, useEffect, useContext } from 'react';
import api from '../../../../services/api';
import Skeleton from 'react-loading-skeleton';
import NavigationButtons from '../../NavigationButtons';
import { formatNumber, parseLocaleNumber } from '../../../../utils/numberFormatter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarSign, faBlender, faMobileAlt, faHome, faLaptop, faBox, faFileAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import 'react-loading-skeleton/dist/skeleton.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import './OfferStep.css';
import './DownpaymentPlans.css';
import { SelectedOfferContext } from '../../../../context/SelectedOfferContext';
import { ClientContext } from '../../../../context/ClientContext';
import { useAuth } from '../../../../context/AuthContext';
import AuthorizationModal from '../../../PurchaseFlow/AuthorizationModal';
import { handleValidationErrors } from '../../../../utils/errorHandler';
import { trackInstallmentPlanSelected, trackOffersSearched } from '../../../../analytics/events';
import { useAnalyticsContext } from '../../../../analytics/useAnalyticsContext';
import {
  groupOffersByInstallments,
  OFFER_LAYOUT_GROUPED_BY_INSTALLMENTS,
  resolveOfferLayout,
} from './offerLayout';

const MySwal = withReactContent(Swal);

const OfferStep = ({ onNext, clientData, setPurchaseRequestId, setPurchaseRequestUid }) => {
  const { setSelectedOffer, setAdvancePaymentState } = useContext(SelectedOfferContext);
  const { clientAuthorization } = useContext(ClientContext);
  const { commerceId, branchId: analyticsBranchId } = useAnalyticsContext();
  const [amount, setAmount] = useState('');
  const [displayAmount, setDisplayAmount] = useState('');
  const [maxAmountState, setMaxAmount] = useState(clientData?.available_credit || 0);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authorizationData, setAuthorizationData] = useState(null); // Estado para autorización manual
  const [categories, setCategories] = useState([]);
  const [authorizationsEnabled] = useState(process.env.REACT_APP_ENABLE_AUTHORIZATIONS === 'true');
  const offerLayout = resolveOfferLayout(process.env.REACT_APP_OFFER_LAYOUT);
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [lastCalculatedAmount, setLastCalculatedAmount] = useState('');
  const [showAuthorizationModal, setShowAuthorizationModal] = useState(false);
  const [requestedAmount, setRequestedAmount] = useState(null);
  const { channelId, branchId } = useAuth();

  const [productNameConfig, setProductNameConfig] = useState('required');
  const [productDescriptionConfig, setProductDescriptionConfig] = useState('optional');
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  
  // Actualizar maxAmountState cuando clientData cambie
  useEffect(() => {
    if (clientData?.available_credit !== undefined) {
      setMaxAmount(clientData.available_credit);
    }
  }, [clientData?.available_credit]);
  
  useEffect(() => {
    const loadCategoriesFromAPI = async () => {
      try {
        const response = await api.get('/industry-categories');
        if (response.data.success && response.data.data.length > 0) {
          return response.data.data.map(category => {
            // Mapear iconos basado en el nombre del icono del backend
            const icon = {
              'faBlender': faBlender,
              'fa-solid fa-mobile': faMobileAlt,
              'fa-solid fa-home': faHome,
              'fa-solid fa-laptop': faLaptop
            }[category.icon] || faBox; // faBox como fallback

            return {
              id: category.id,
              label: category.label,
              icon: icon
            };
          });
        }
        return null;
      } catch (error) {
        console.warn('Failed to load categories from API, falling back to environment variables:', error.message);
        return null;
      }
    };

    const loadCategoriesFromEnv = () => {
      const categoriesConfig = process.env.REACT_APP_PRODUCT_CATEGORIES;
      if (!categoriesConfig) return [];

      return categoriesConfig.split(',').map(category => {
        const [id, label, iconName] = category.split(':');
        const icon = {
          faBlender,
          faMobileAlt,
          faHome,
          faLaptop
        }[iconName] || faBlender;

        return { id, label, icon };
      });
    };

    const loadCategories = async () => {
      setCategoriesLoading(true);
      
      // Intentar cargar desde API primero
      const apiCategories = await loadCategoriesFromAPI();
      
      if (apiCategories && apiCategories.length > 0) {
        console.log('Categories loaded from API');
        setCategories(apiCategories);
      } else {
        // Fallback a variables de entorno
        console.log('Categories loaded from environment variables');
        const envCategories = loadCategoriesFromEnv();
        setCategories(envCategories);
      }
      
      setCategoriesLoading(false);
    };

    // Cargar configuración de campos de producto
    const loadProductFieldConfig = () => {
      const nameConfig = process.env.REACT_APP_PRODUCT_NAME || 'required';
      const descriptionConfig = process.env.REACT_APP_PRODUCT_DESCRIPTION || 'optional';
      
      setProductNameConfig(nameConfig);
      setProductDescriptionConfig(descriptionConfig);
    };

    loadCategories();
    loadProductFieldConfig();
  }, []);

  const handleRequestAuthorization = async (payload) => {
    try {
      // Crear FormData para enviar archivo adjunto
      const formData = new FormData();
      formData.append('commerce_id', channelId);
      formData.append('channel_branch_id', branchId);
      formData.append('amount', payload.amount);
      formData.append('observation', payload.observation);
      
      if (payload.password) {
        formData.append('password', payload.password);
      }
      
      if (payload.attachment) {
        formData.append('attachment', payload.attachment);
      }

      const response = await api.post(`/customers/${clientData.id}/authorizations`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.status === 201) {
        setShowAuthorizationModal(false);
        
        MySwal.fire({
          title: '¡Listo!',
          text: response.data.message,
          icon: response.data.authorization.status === 'approved' ? 'success' : 'info',
        });

        if(response.data.authorization.status === 'approved') {
        }
        
        // Return response data so modal can track authorization status
        return response.data;
      }



      MySwal.fire({
        title: 'Solicitud enviada',
        text: 'Enviamos tu solicitud. Esperá a que la aprueben.',
        icon: 'info',
      });
      
      // Return response data so modal can track authorization status
      return response.data;
    } catch (error) {
      console.error('Error al solicitar autorización:', error);
      const errorConfig = handleValidationErrors(error);
      MySwal.fire(errorConfig);
      throw error; // Re-lanzar el error para que el modal pueda resetear el estado de loading
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9,]/g, '');
    
    if (value === '') {
      setAmount('');
      setDisplayAmount('');
      setPlans([]);
      setSelectedPlan(null);
      setLastCalculatedAmount('');
      return;
    }

    // Convertir al formato español para mostrar
    const numericValue = parseLocaleNumber(value);
    if (!isNaN(numericValue)) {
      setAmount(numericValue.toString());
      setDisplayAmount(formatNumber(numericValue));
      setPlans([]);
      setSelectedPlan(null);
    }
  };

  const handleRequestMoreCredit = (e) => {
    e?.preventDefault();
    setRequestedAmount(null);
    setShowAuthorizationModal(true);
  };

  const handleAmountValidation = () => {
    const numericAmount = parseFloat(amount);
    if (!numericAmount || numericAmount <= 0) {
      MySwal.fire({
        icon: 'warning',
        title: 'Monto inválido',
        text: 'Ingresá un monto válido.',
      });
      return false;
    }

    if (numericAmount > maxAmountState) {
      if (authorizationsEnabled) {
        MySwal.fire({
          icon: 'warning',
          title: 'Supera el límite del cliente',
          html: `
            El monto supera el límite disponible del cliente por $${formatNumber(numericAmount - maxAmountState)}.<br><br>
            ¿Querés solicitar una autorización por $${formatNumber(numericAmount)}?
          `,
          showConfirmButton: true,
          confirmButtonText: `Solicitar autorización por $${formatNumber(numericAmount)}`,
          showCancelButton: true,
          cancelButtonText: 'Cancelar'
        }).then((result) => {
          if (result.isConfirmed) {
            setRequestedAmount(numericAmount);
            setShowAuthorizationModal(true);
          }
        });
      } else {
        MySwal.fire({
          icon: 'error',
          title: 'Supera el límite del cliente',
          text: `El monto no puede superar el límite disponible del cliente: $${formatNumber(maxAmountState)}.`,
          confirmButtonText: 'Entendido'
        });
      }
      return false;
    }

    return true;
  };

  const fetchPlans = async () => {
    if (!handleAmountValidation()) return;

    // Validar que el monto haya cambiado
    if (amount === lastCalculatedAmount) {
      MySwal.fire({
        icon: 'info',
        title: 'Monto sin cambios',
        text: 'Modificá el monto para calcular nuevas cuotas.',
      });
      return;
    }

    if (!selectedCategory && categories.length > 0) {
      MySwal.fire({
        icon: 'warning',
        title: 'Elegí una categoría',
        text: 'Seleccioná la categoría del producto.',
      });
      return;
    }

    if (productNameConfig === 'required' && !productName.trim()) {
      MySwal.fire({
        icon: 'warning',
        title: 'Falta el nombre del producto',
        text: 'Ingresá el nombre del producto.',
      });
      return;
    }

    // Validación dinámica de la descripción del producto
    if (productDescriptionConfig === 'required' && !productDescription.trim()) {
      MySwal.fire({
        icon: 'warning',
        title: 'Falta la descripción del producto',
        text: 'Ingresá la descripción del producto.',
      });
      return;
    }

    setLoading(true);
    try {
      // Buscar la categoría seleccionada en el array de categorías para obtener su información
      const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);
      
      const requestPayload = {
        amount: parseFloat(amount),
      };

      if (selectedCategoryData && selectedCategoryData.id) {
        requestPayload.industry_category_id = selectedCategoryData.id;
      }

      // Agregar campos de producto según la configuración
      if (productNameConfig !== 'hidden') {
        requestPayload.product_name = productName.trim() || null;
      }
      
      if (productDescriptionConfig !== 'hidden') {
        requestPayload.product_description = productDescription.trim() || null;
      }

      const response = await api.post(`/customers/${clientData.id}/purchase-requests`, requestPayload);

      // Track offers searched
      if (commerceId) {
        trackOffersSearched({
          commerceId,
          branchId: analyticsBranchId,
          amount: parseFloat(amount),
          productName: productName.trim() || undefined,
          productCategory: selectedCategoryData?.name || undefined,
          customerDni: clientData?.dni
        });
      }

      setPurchaseRequestId(response.data.id);
      if (typeof setPurchaseRequestUid === 'function') {
        setPurchaseRequestUid(response.data.uid || null);
      }
      const updatedOffers = response.data.offers.map((offer) => ({
        ...offer,
        purchase_request_id: response.data.id,
      }));

      // Ordenar todos los planes juntos por cantidad de cuotas
      const sortedOffers = updatedOffers.sort((a, b) => a.installments - b.installments);

      setLastCalculatedAmount(amount); // Guardar el monto calculado
      setPlans(sortedOffers);
      setSelectedPlan(null); // Asegurar que no hay plan seleccionado

      if (sortedOffers.length === 0) {
        MySwal.fire({
          icon: 'info',
          title: 'No hay cuotas disponibles',
          html: `
            <div class="text-start">
              <p class="mb-3">
                Por ahora no hay planes en cuotas para este monto. Puede ser porque:
              </p>
              <ul class="list-unstyled">
                <li class="mb-2">
                  <i class="bi bi-circle-fill me-2 text-primary" style="font-size: 8px;"></i>
                  El monto supera la cuota mensual máxima del cliente
                </li>
                <li class="mb-2">
                  <i class="bi bi-circle-fill me-2 text-primary" style="font-size: 8px;"></i>
                  El monto es menor al mínimo para pagar en cuotas
                </li>
                <li class="mb-2">
                  <i class="bi bi-circle-fill me-2 text-primary" style="font-size: 8px;"></i>
                  El cliente tiene una restricción temporal en su crédito
                </li>
              </ul>
              <p class="mt-3">
                Podés:
              </p>
              <ul class="list-unstyled">
                <li class="mb-2">
                  <i class="bi bi-check2 me-2 text-success"></i>
                  Probar con otro monto
                </li>
                <li class="mb-2">
                  <i class="bi bi-check2 me-2 text-success"></i>
                  Ofrecer otra forma de pago
                </li>
                ${authorizationsEnabled ? `
                <li class="mb-2">
                  <i class="bi bi-check2 me-2 text-success"></i>
                  <a href="#" onclick="return false;" class="text-decoration-none" id="requestMoreCredit">
                    Solicitar una autorización especial
                  </a>
                </li>
                ` : ''}
              </ul>
            </div>
          `,
          confirmButtonText: 'Entendido',
          showCloseButton: true,
          didOpen: () => {
            if (authorizationsEnabled) {
              document.getElementById('requestMoreCredit').addEventListener('click', () => {
                MySwal.close();
                setShowAuthorizationModal(true);
              });
            }
          }
        });
      }
    } catch (error) {
      console.error('Error al crear la PurchaseRequest:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No pudimos calcular las cuotas. Intentá de nuevo.',
      });
    } finally {
      setLoading(false);
    }
  };

  const selectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  const handleNext = async () => {
    if (!selectedPlan) {
      Swal.fire({
        icon: 'warning',
        title: 'Elegí un plan',
        text: 'Seleccioná un plan antes de continuar.',
      });
      return;
    }

    // Validar que el monto actual coincida con el monto calculado
    if (amount !== lastCalculatedAmount) {
      Swal.fire({
        icon: 'warning',
        title: 'Monto modificado',
        text: 'Cambió el monto. Calculá las cuotas de nuevo.',
      });
      return;
    }

    try {
      const applicantId = clientData?.id;

      await api.post(
        `/customers/${applicantId}/purchase-requests/${selectedPlan.purchase_request_id}/select-offer`,
        { offer_id: selectedPlan.id }
      );

      // Guardar la oferta seleccionada en el contexto
      setSelectedOffer({
        amount: selectedPlan.financed_amount,
        originalAmount: parseFloat(amount),
        term: selectedPlan.installments,
        interestRate: selectedPlan.interest_rate,
        monthlyPayment: selectedPlan.installment_amount,
        firstDueDate: selectedPlan.first_due_date_formatted,
        displayDate: selectedPlan.display_date_formatted,
        isDownpaymentPlan: selectedPlan.is_downpayment_plan,
        downpaymentAmount: selectedPlan.downpayment_amount,
        downpaymentType: selectedPlan.downpayment_type,
        downpaymentValue: selectedPlan.downpayment_value,
      });
      setAdvancePaymentState({
        method: null,
        status: 'pending',
        payload: null,
      });

      // Track installment plan selected
      if (commerceId) {
        trackInstallmentPlanSelected({
          commerceId,
          branchId: analyticsBranchId,
          amount: selectedPlan.financed_amount,
          installments: selectedPlan.installments,
          interestRate: selectedPlan.interest_rate,
          customerDni: clientData?.dni,
          isDownpaymentPlan: selectedPlan.is_downpayment_plan || false,
          downpaymentType: selectedPlan.is_downpayment_plan ? selectedPlan.downpayment_type : undefined,
          downpaymentValue: selectedPlan.is_downpayment_plan ? selectedPlan.downpayment_value : undefined,
          downpaymentAmount: selectedPlan.is_downpayment_plan ? selectedPlan.downpayment_amount : undefined,
          originalAmount: selectedPlan.is_downpayment_plan ? parseFloat(amount) : undefined
        });
      }

      onNext({ selectedPlan });
    } catch (error) {
      console.error('Error al seleccionar la oferta:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un problema al elegir el plan. Intentá de nuevo.',
      });
    }
  };

  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchPlans();
    }
  };

  const isInterestFreePlan = (plan) => {
    const rate = plan?.rate ?? plan?.interest_rate;
    return Number(rate) === 0;
  };

  const TECHNICAL_PLAN_NAME_LABELS = {
    'Anticipo de Cuota': 'Con pago inicial',
    'Anticipo de cuota': 'Con pago inicial',
  };

  const getPlanBadgeLabel = (plan) => {
    if (plan?.is_downpayment_plan) {
      if (plan.downpayment_type === 'percentage' && plan.downpayment_amount) {
        return `Pago inicial: $${formatNumber(plan.downpayment_amount)}`;
      }

      if (plan.downpayment_type === 'installment' && plan.downpayment_value) {
        const installments = Number(plan.downpayment_value);
        return `Pago inicial: ${installments} cuota${installments > 1 ? 's' : ''}`;
      }
    }

    const planName = (plan?.plan_name || '').trim();
    return TECHNICAL_PLAN_NAME_LABELS[planName] || planName;
  };

  const getFirstInstallmentLabel = (plan) => {
    if (plan.is_downpayment_plan && plan.downpayment_type === 'installment') {
      const installmentNumber = Math.min(Number(plan.downpayment_value) + 1, plan.installments);
      return `${installmentNumber}ª cuota: ${plan.display_date_formatted}`;
    }

    return `Primera cuota: ${plan.display_date_formatted}`;
  };

  const getOfferCardClassName = (plan) => [
    'card offer-option',
    selectedPlan?.id === plan.id ? 'selected' : '',
    plan.is_downpayment_plan ? 'downpayment-plan' : '',
    isInterestFreePlan(plan) ? 'interest-free-plan' : '',
    getPlanBadgeLabel(plan) || isInterestFreePlan(plan) ? 'has-top-badges' : ''
  ].filter(Boolean).join(' ');

  const renderPlanNameBadge = (plan) => {
    const planBadgeLabel = getPlanBadgeLabel(plan);
    return planBadgeLabel ? <span className="plan-name-badge">{planBadgeLabel}</span> : null;
  };

  const renderPlanCard = (plan) => (
    <div className="col-md-4" key={plan.id}>
      <div
        className={getOfferCardClassName(plan)}
        onClick={() => selectPlan(plan)}
        style={{ cursor: 'pointer' }}
      >
        {renderPlanNameBadge(plan)}
        {isInterestFreePlan(plan) && (
          <span className="interest-free-badge">Sin interés</span>
        )}
        <div className="card-body text-center">
          <h4 className="card-text">{plan.installments} cuotas</h4>
          <p className="card-title mb-3">
            {`de $${formatNumber(plan.installment_amount)}`}
          </p>
          <p className="card-text">
            {getFirstInstallmentLabel(plan)}
          </p>
        </div>
      </div>
    </div>
  );

  const renderSkeletons = () => {
    return (
      <div className="row justify-content-center mt-5">
        {[...Array(3)].map((_, index) => (
          <div className="col-md-4" key={index}>
            <div className="card offer-option">
              <div className="card-body text-center">
                <Skeleton height={30} width={150} />
                <Skeleton height={20} width={100} className="mt-2" />
                <Skeleton height={20} width={180} className="mt-2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCategorySkeleton = () => {
    return (
      <div className="row justify-content-center">
        {[...Array(3)].map((_, index) => (
          <div className="col-md-3" key={index}>
            <div className="card category-option">
              <div className="card-body text-center">
                <Skeleton height={40} width={40} className="mb-3" />
                <Skeleton height={24} width={120} />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="row pt-3 d-flex justify-content-center">
      <h4 className="text-center mb-4">¿Qué producto vas a vender?</h4>

      {/* Skeleton de Categorías - Se muestra mientras se cargan */}
      {categoriesLoading && renderCategorySkeleton()}

      {/* Selector de Categorías - Solo se muestra si hay categorías configuradas y ya cargaron */}
      {!categoriesLoading && categories.length > 0 && !selectedCategory && (
        <div className="row justify-content-center">
          {categories.map((category) => (
            <div className="col-md-3" key={category.id}>
              <div
                className={`card category-option ${selectedCategory === category.id ? 'selected' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-body text-center">
                  <FontAwesomeIcon icon={category.icon} size="2x" className="mb-3" />
                  <h5 className="card-title">{category.label}</h5>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Campos de producto y monto */}
      {!categoriesLoading && (selectedCategory || categories.length === 0) && (
        <div className="col-md-8">

          {/* Campo de nombre del producto - Solo se muestra si no está oculto */}
          {productNameConfig !== 'hidden' && (
            <div className="form-group mb-3">
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faBox} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nombre del producto (ej.: Heladera Samsung)"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  required={productNameConfig === 'required'}
                />
              </div>
            </div>
          )}

          {/* Campo de descripción del producto - Solo se muestra si no está oculto */}
          {productDescriptionConfig !== 'hidden' && (
            <div className="form-group mb-3">
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faFileAlt} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={`Descripción ${productDescriptionConfig === 'required' ? '(requerido)' : '(opcional)'}`}
                  value={productDescription}
                  onChange={(e) => setProductDescription(e.target.value)}
                  required={productDescriptionConfig === 'required'}
                />
              </div>
            </div>
          )}

          {/* Input de monto - Se muestra si hay categoría seleccionada o no hay categorías configuradas */}
          <div className="input-group">
            <span className="input-group-text">
              <FontAwesomeIcon icon={faDollarSign} />
            </span>
            <input
              type="text"
              className="form-control"
              id="amount"
              name="amount"
              autoComplete="off"
              placeholder="¿Cuál es el precio?"
              required
              value={displayAmount}
              onChange={handleAmountChange}
              onKeyPress={handleInputKeyPress}
            />
            <button 
              className="btn btn-primary" 
              type="button" 
              onClick={fetchPlans}
              disabled={amount === lastCalculatedAmount || !amount} // Deshabilitar si el monto no cambió o está vacío
            >
              Calcular cuotas
            </button>
          </div>
          <p>
            <small className="text-muted">
              {`Límite máximo del cliente (${clientAuthorization?.status === 'approved' ? 'autorizado' : 'disponible'}): $${formatNumber(maxAmountState)}.`}
              {authorizationsEnabled && (
                <a href="#" onClick={handleRequestMoreCredit}>
                  Solicitá más cupo
                </a>
              )}
            </small>
          </p>
        </div>
      )}

      {loading ? (
        renderSkeletons()
      ) : (
        plans.length > 0 && (
          <>
            {offerLayout === OFFER_LAYOUT_GROUPED_BY_INSTALLMENTS ? (
              <div id="plansContainer" className="mt-5">
                {groupOffersByInstallments(plans).map((group) => (
                  <div className="row justify-content-center" key={group.installments}>
                    {group.plans.map(renderPlanCard)}
                  </div>
                ))}
              </div>
            ) : (
              <div id="plansContainer" className="row justify-content-center mt-5">
                {plans.map(renderPlanCard)}
              </div>
            )}

            {/* Mensaje de regla de adelanto*/}
            {plans.some(plan => plan.is_downpayment_plan) && (
              <div className="row justify-content-center mt-3">
                <div className="col-md-12">
                  <div className="m-0 px-3 py-2 shadow rounded-2 text-primary" role="alert" style={{ background: '#f0f9ff'}}>
                    <div className="d-flex align-items-start">
                      <div>
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        <strong>Importante</strong>
                        <br/>
                        <p>Los planes con pago inicial se cobran al confirmar la compra.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <NavigationButtons onNext={handleNext} nextLabel="Continuar" />
            
          </>
        )
      )}

      <AuthorizationModal
        show={showAuthorizationModal}
        onClose={() => setShowAuthorizationModal(false)}
        onRequest={handleRequestAuthorization}
        initialAmount={requestedAmount}
        customerDni={clientData?.dni}
        reason={requestedAmount ? 'amount_exceeds_limit' : 'manual_request'}
      />
    </div>
  );
};

export default OfferStep;
